import { LeagueConfig, Environment } from '@leagueplatform/core';
import { createReactRouterAdapter } from '@leagueplatform/react-router-adapter';
import { getCaregiverImpersonationState } from '@leagueplatform/caregiver';
import { APP_ID } from 'common/constants';
import { LoadingSpinner } from 'components/loading-spinner/loading-spinner.component';
import EN_LOCALES from 'locales/en.json';
import ZH_LOCALES from 'locales/zh.json';
import FR_CA_LOCALES from 'locales/fr-CA.json';
import AR_LOCALES from 'locales/ar.json';
import HI_IN_LOCALES from 'locales/hi-IN.json';
import PA_IN_LOCALES from 'locales/pa-IN.json';
import UR_PK_LOCALES from 'locales/ur-PK.json';
import FeatureHighlightBackground from 'assets/feature-highlight-background.svg';
import ConnectionErrorStateImage from 'assets/connection-error.svg';
import { trilliumTheme } from 'theme/theme';
import { MasonryErrorFallback } from 'components/masonry-error-fallback/masonry-error-fallback.component';

const {
  VITE_WS_API_URL,
  VITE_API_URL,
  VITE_LEGACY_REST_API_URL,
  VITE_CONTENT_URL,
  VITE_AUTH0_DOMAIN,
  VITE_AUTH0_CLIENT_ID,
  VITE_AUTH0_AUDIENCE,
  VITE_APP_ENV,
} = import.meta.env;

const authConfig = {
  domain: VITE_AUTH0_DOMAIN!,
  client_id: VITE_AUTH0_CLIENT_ID!,
  redirect_uri: window.location.origin,
  audience: VITE_AUTH0_AUDIENCE,
  ...getCaregiverImpersonationState(),
};

const apiConfig = {
  wsUrl: VITE_WS_API_URL!,
  url: VITE_API_URL!,
  legacyRestApi: VITE_LEGACY_REST_API_URL!,
};

export const config: LeagueConfig = {
  core: {
    clientId: APP_ID,
    auth: { clientOptions: authConfig, idleSessionLifetimeHours: 0.25 }, // Default 15 minutes, should match the auth0 session inactivity timeout
    api: apiConfig,
    appEnvironment: VITE_APP_ENV as Environment,
    contentUrl: VITE_CONTENT_URL!,
    routing: { adapter: createReactRouterAdapter() },
    ui: {
      theme: trilliumTheme,
      components: {
        pageHeader: {
          shape: 'line',
          backgroundColor: 'surface.screenHeader.background',
        },
        loading: {
          element: <LoadingSpinner />,
          text: false,
        },
      },
    },
    i18n: {
      strings: {
        default: EN_LOCALES,
        en: EN_LOCALES,
        zh: ZH_LOCALES,
        fr: FR_CA_LOCALES,
        ar: AR_LOCALES,
        hi: HI_IN_LOCALES,
        pa: PA_IN_LOCALES,
        ur: UR_PK_LOCALES,
      },
    },
  },
  assets: {
    overrides: {
      FEATURE_HIGHLIGHT_BACKGROUND: FeatureHighlightBackground,
      WEB_COMMON_ERROR_STATE_IMAGE: ConnectionErrorStateImage,
      ERROR_STATE_COVER: ConnectionErrorStateImage,
    },
  },
  claims: {
    enableDynamicFilters: true,
    featureFlags: {
      shouldShowClaimDetails: true,
    },
  },
  benefits: {
    benefitsLandingMasonryConfigKey: 'benefits$landingPage$root',
    benefitsDetailsMasonryConfigKey: 'benefits$detailPage$root',
  },
  careDiscovery: {
    masonryAppVersion: '2.0',
    masonryAppId: {
      left: 'careLandingPage$root',
    },
    showGetCareLandingHeadingText: false,
  },
  careDocuments: {
    shouldUseDocumentsV2: true,
  },
  careFinder: {
    schedulingEnabled: true,
  },
  masonry: {
    rootErrorFallback: MasonryErrorFallback,
  },
  healthJourney: {
    activityDetailsConfig: {
      enableFullPageBackground: true,
    },
  },
  wallet: {
    showLandingPageClaimsEntry: true,
  },
  __internal: {
    shouldApplyCSSReset: true,
    shouldApplyLegacyGlobalStyles: true,
    shouldIgnoreDOMLangAttributeForLocale: true,
  },
};
